import React, { useRef } from 'react';
import { ShieldIcon, StarIcon, CheckCircle2Icon, MessageCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { handleCartClick, CART_URL } from '../utils/metaPixel';
interface HeroSectionProps {
  onVideoReady?: () => void;
  showRestOfPage?: boolean;
}
export function HeroSection({
  onVideoReady,
  showRestOfPage = true
}: HeroSectionProps) {
  const maxTimeRef = useRef(0);
  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (!video.seeking) {
      if (video.currentTime > maxTimeRef.current) {
        maxTimeRef.current = video.currentTime;
      }
    }
    if (video.duration) {
      const progress = video.currentTime / video.duration;
      if (progress >= 0.9 && onVideoReady) {
        onVideoReady();
      }
    }
  };
  const handleSeeking = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    // Prevent skipping ahead by more than 1 second from the max time watched
    if (video.currentTime > maxTimeRef.current + 1) {
      video.currentTime = maxTimeRef.current;
    }
  };
  return <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-dark to-[#1f1f35] overflow-hidden relative" data-id="element-112">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-113">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-rose-900/30 rounded-full blur-3xl" data-id="element-114"></div>
        <div className="absolute top-1/2 -left-24 w-72 h-72 bg-gold-900/20 rounded-full blur-3xl" data-id="element-115"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-116">
        <div className="flex flex-col items-center" data-id="element-117">
          {/* Main Content: Centered */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center space-y-6 text-center max-w-3xl" data-id="element-118">
            <div className="flex items-center space-x-1" data-id="element-119">
              {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-5 h-5 text-gold-500 fill-gold-500" data-id="element-120" />)}
              <span className="ml-2 text-sm font-medium text-gray-400" data-id="element-121">
                Más de 5,000 mujeres felices
              </span>
            </div>

            <h1 className="md:text-5xl lg:text-6xl font-bold text-white text-[32px]" data-id="element-122">
              Deja de escribir como todas <br data-id="element-123" />
              <span className="text-rose-500" data-id="element-124">
                Haz que ÉL vuelva a buscarte sin que lo persigas
              </span>
            </h1>

            <p className="md:text-2xl font-medium text-gray-300 font-['Tiro_Gurmukhi',_serif] text-[20px]" data-id="element-125">
              Domina los mensajes que activan su interés, despiertan su deseo y
              hacen que piense en ti en menos de 2 horas.
            </p>

            <div className="w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden border-2 border-rose-500/20 relative aspect-[9/16] sm:aspect-video bg-black" data-id="element-126">
              <video src="https://cdn.magicpatterns.com/uploads/ephcTSyuNf6hyTNNQvYvsQ/0403(2).mp4" controls controlsList="nodownload" className="absolute inset-0 w-full h-full object-contain" onTimeUpdate={handleTimeUpdate} onSeeking={handleSeeking} poster="https://cdn.magicpatterns.com/uploads/8XqppsigHBkPiTUhnt1zty/Mockup_principal.optimizado.webp" data-id="element-127" />
            </div>

            <p className="text-gray-400 text-[16px] leading-relaxed" data-id="element-128">
              Descubre <strong data-id="element-129">EXACTAMENTE</strong> qué decir en cada momento
              para despertar
              <em data-id="element-130"> deseo, intriga y conexión real</em>…{' '}
              <br className="hidden sm:block" data-id="element-131" />
              <span className="underline decoration-rose-500/50 underline-offset-4" data-id="element-132">
                sin parecer necesitada ni forzada.
              </span>
            </p>

            {showRestOfPage && <>
                <div className="flex flex-wrap justify-center gap-3 py-2" data-id="element-133">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-200 text-sm font-semibold border border-rose-500/30" data-id="element-134">
                    <CheckCircle2Icon className="w-4 h-4 mr-1.5" data-id="element-135" />
                    [Mensajes Probados]
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-200 text-sm font-semibold border border-rose-500/30" data-id="element-136">
                    <MessageCircleIcon className="w-4 h-4 mr-1.5" data-id="element-137" />
                    +100 Frases
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-200 text-sm font-semibold border border-rose-500/30" data-id="element-138">
                    <ShieldIcon className="w-4 h-4 mr-1.5" data-id="element-139" />
                    Método Paso a Paso
                  </span>
                </div>

                <div className="pt-4 flex flex-col items-center space-y-3" data-id="element-140">
                  <a href={CART_URL} onClick={handleCartClick} className="w-full sm:w-auto text-center bg-rose-700 hover:bg-rose-800 text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 animate-pulse-slow text-lg" data-id="element-141">
                    👉 QUIERO QUE ÉL ME BUSQUE AHORA
                  </a>
                  <div className="flex items-center text-sm text-gray-400 font-medium" data-id="element-142">
                    <span className="flex items-center" data-id="element-143">
                      <ShieldIcon className="w-4 h-4 mr-1 text-green-500" data-id="element-144" />{' '}
                      Acceso inmediato
                    </span>
                    <span className="mx-2" data-id="element-145">•</span>
                    <span data-id="element-146">Pago único</span>
                    <span className="mx-2" data-id="element-147">•</span>
                    <span data-id="element-148">Garantía 7 días</span>
                  </div>
                </div>
              </>}
          </motion.div>
        </div>

        {/* Stats Row */}
        {showRestOfPage && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center border-t border-white/10 pt-8" data-id="element-149">
            <div className="flex flex-col items-center" data-id="element-150">
              <span className="text-4xl font-heading font-bold text-gold-400 mb-2" data-id="element-151">
                +100
              </span>
              <span className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-152">
                Mensajes Listos pdf
              </span>
            </div>
            <div className="flex flex-col items-center" data-id="element-153">
              <span className="text-4xl font-heading font-bold text-gold-400 mb-2" data-id="element-154">
                3
              </span>
              <span className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-155">
                Pasos Probados
              </span>
            </div>
            <div className="flex flex-col items-center" data-id="element-156">
              <span className="text-4xl font-heading font-bold text-gold-400 mb-2" data-id="element-157">
                +20 videos
              </span>
              <span className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-158">
                paso a paso
              </span>
            </div>
          </motion.div>}
      </div>
    </section>;
}